import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ChatConfig,
  ChatCommand,
  DEFAULT_CHAT_COMMANDS,
  User,
  Tournament,
  CanalTorneoService,
  EstadoConexion,
} from '@monou/repo-bot-lib';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  // Form inputs
  idUsuario = '';
  idTorneo = '';

  // UI state
  conectado = false;
  cargando = false;
  errorMsg = '';
  estadoConexion = EstadoConexion.DESCONECTADO;
  EstadoConexion = EstadoConexion;
  esAdmin = false;
  welcomeMessage = '';

  // Chat data
  usuarioActual: User | null = null;
  tournamentForChat: Tournament | null = null;
  tokenGenerado = '';

  chatConfig: Partial<ChatConfig> = {
    showUserList: true,
    theme: 'dark' as const,
    allowEmojis: true,
    maxMessageLength: 500,
  };

  chatCommands: ChatCommand[] = DEFAULT_CHAT_COMMANDS;

  private readonly TOURNAMENT_API = '/monou-api/api/tournament';
  private destroy$ = new Subject<void>();

  constructor(
    private canalTorneoService: CanalTorneoService,
    private http: HttpClient,
  ) {}

  ngOnInit(): void {
    this.canalTorneoService.estadoConexion$
      .pipe(takeUntil(this.destroy$))
      .subscribe(estado => {
        this.estadoConexion = estado;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Generates a fake JWT token (no cryptographic signature — matches MockTokenValidator on backend) */
  private generarToken(userId: number, torneoId: number): string {
    const header = btoa(JSON.stringify({ alg: 'none', typ: 'JWT' }))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    const now = Math.floor(Date.now() / 1000);
    const payload = btoa(JSON.stringify({
      sub: String(userId),
      userId,
      tournamentId: torneoId,
      iat: now,
      exp: now + 3600,
    })).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    return `${header}.${payload}.fakesig`;
  }

  conectar(): void {
    const userId = Number(this.idUsuario);
    const torneoId = Number(this.idTorneo);

    if (!userId || userId <= 0) {
      this.errorMsg = 'Ingresa un ID de usuario válido';
      return;
    }
    if (!torneoId || torneoId <= 0) {
      this.errorMsg = 'Ingresa un ID de torneo válido';
      return;
    }

    this.errorMsg = '';
    this.cargando = true;

    this.http
      .get<any>(`${this.TOURNAMENT_API}/${torneoId}/`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          try {
            const tournamentData = response?.data?.[0]?.[0]?.[0];
            if (!tournamentData?.tournament) {
              throw new Error('Torneo no encontrado en la respuesta');
            }

            const t = tournamentData.tournament;

            this.tournamentForChat = t as Tournament;
            this.esAdmin = t.user_id === userId;
            this.tokenGenerado = this.generarToken(userId, torneoId);
            this.welcomeMessage = `Bienvenido a <strong>${t.name}</strong>`;

            this.usuarioActual = {
              id: userId,
              user_id: userId,
              username: `Player_${userId}`,
              monedas: 0,
              image_url: `https://ui-avatars.com/api/?name=P${userId}&background=704eff&color=fff`,
              roles: ['user'],
              evidence_url: null,
              high_school: null,
              totalplay_contract_number: null,
            } as unknown as User;

            this.conectado = true;
          } catch (e: any) {
            this.errorMsg = e?.message || 'Error al procesar la respuesta del torneo';
          }
          this.cargando = false;
        },
        error: (err) => {
          this.errorMsg =
            err.status === 0
              ? 'No se pudo conectar con la API (CORS o red)'
              : `Error ${err.status}: No se encontró el torneo`;
          this.cargando = false;
        },
      });
  }

  desconectar(): void {
    this.canalTorneoService.desconectar();
    this.conectado = false;
    this.tournamentForChat = null;
    this.usuarioActual = null;
    this.tokenGenerado = '';
    this.errorMsg = '';
    this.esAdmin = false;
    this.welcomeMessage = '';
  }

  getEstadoLabel(): string {
    switch (this.estadoConexion) {
      case EstadoConexion.CONECTADO: return 'Conectado';
      case EstadoConexion.CONECTANDO: return 'Conectando…';
      case EstadoConexion.ERROR: return 'Error de conexión';
      default: return 'Desconectado';
    }
  }

  getEstadoClass(): string {
    switch (this.estadoConexion) {
      case EstadoConexion.CONECTADO: return 'estado-conectado';
      case EstadoConexion.CONECTANDO: return 'estado-conectando';
      case EstadoConexion.ERROR: return 'estado-error';
      default: return 'estado-desconectado';
    }
  }
}
